import React, { useState, useRef, useEffect } from "react";

/**
 * Want, with a say in where it lands.
 *
 * It used to add to whichever list was "active" — which is just the one you
 * last created or clicked — so cards quietly piled into the newest list. With
 * one list there's nothing to decide and it still adds straight away; with
 * several it asks, and names them.
 */
export default function AddToListButton({
  lists = [],
  onAdd,               // (listId) => void
  onList,              // truthy when the card is already on one of them
  onListName,          // which list it's already on, for the label's title
  label = "Want",
  className = "btn btn-want",
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (onList) {
    return (
      <button className={className} disabled title={onListName ? `On "${onListName}"` : undefined}>
        On want list
      </button>
    );
  }

  // No lists at all: say what's missing rather than failing silently, which is
  // what the old button did when nothing had been created yet.
  if (lists.length === 0) {
    return (
      <button className={className} disabled title="Make a list first, under Collection">
        No lists yet
      </button>
    );
  }

  if (lists.length === 1) {
    return (
      <button className={className} onClick={() => onAdd(lists[0].id)} title={`Add to "${lists[0].name}"`}>
        {label}
      </button>
    );
  }

  return (
    <span className="add-to-list" ref={wrapRef}>
      <button
        className={className}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Choose which list"
      >
        {label} ▾
      </button>
      {open && (
        <div className="add-to-list-menu" role="menu">
          {lists.map((l) => (
            <button
              key={l.id}
              role="menuitem"
              className="add-to-list-item"
              onClick={() => { onAdd(l.id); setOpen(false); }}
            >
              {l.name}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
